<template>
  <nav>
    <div :class="infoClass" v-if="informationVisible || limitVisible">
      <span v-if="informationVisible" class="small">
        {{$t("pagination.info", {count: count, start: startCount, end: endCount})}}
      </span>
      <div v-if="limitVisible" class="ms-auto">
        <span>{{$t("pagination.display")}}</span>
        <select v-model="limitCount"
                v-on:change="limitChange">
          <option>5</option>
          <option>10</option>
          <option>20</option>
          <option>50</option>
          <option>100</option>
        </select>
      </div>
    </div>
    <div :class="longClass">
      <ul class="justify-content-center m-0"
          :class="navSize">
        <li role="button"
            class="page-item user-select-none"
            :class="prevButtonActiveClass"
            v-on:click="movePrevent();"
            v-show="prevButtonVisible">
          <span class="page-link">&laquo;</span>
        </li>
        <li class="px-2"
            v-show="overStartRange">...</li>
        <li role="button"
            v-for="n in pageCount" :key="n"
            class="page-item user-select-none"
            :class="{active: selectedPageNumber === n}"
            v-on:click="movePage(n);"
            v-show="inRange(n)">
          <span class="page-link">{{n}}</span>
        </li>
        <li class="px-2"
            v-show="overEndRange">...</li>
        <li role="button"
            class="page-item user-select-none"
            :class="nextButtonActiveClass"
            v-on:click="moveNext();"
            v-show="nextButtonVisible">
          <span class="page-link">&raquo;</span>
        </li>
      </ul>
    </div>
    <div :class="shortClass">
      <ul class="justify-content-between align-items-center m-0"
          :class="shortNavSize">
        <li role="button"
            class="page-item user-select-none"
            :class="prevButtonActiveClass"
            v-on:click="movePrevent();">
          <span class="page-link">&laquo;</span>
        </li>
        <li class="text-nowrap">
          {{selectedPageNumber}}
          /
          {{pageCount}}
        </li>
        <li type="button"
            class="page-item user-select-none"
            :class="nextButtonActiveClass"
            v-on:click="moveNext();">
          <span class="page-link">&raquo;</span>
        </li>
      </ul>
    </div>
  </nav>
</template>

<script>
  import { defineComponent } from "vue";

  export default defineComponent({
    props: {
      count: {
        type: Number,
        default: 0,
        validator: (val) => {
          return 0 <= val;
        },
      },
      limit: {
        type: Number,
        default: 20,
        validator: (val) => {
          return 0 <= val;
        },
      },
      offset: {
        type: Number,
        default: 0,
        validator: (val) => {
          return 0 <= val;
        },
      },
      breakpoint: {
        type: String,
        default: null,
        validator: (val) => {
          return [null, "sm", "md", "lg", "xl", "xxl"].includes(val);
        },
      },
      size: {
        type: String,
        default: null,
        validator: (val) => {
          return [null, "sm", "lg"].includes(val);
        },
      },
      shortSize: {
        type: String,
        default: null,
        validator: (val) => {
          return [null, "sm", "lg"].includes(val);
        },
      },
      visibleInfo: {
        type: Boolean,
        default: true,
      },
      visibleLimit: {
        type: Boolean,
        default: true,
      },
      range: {
        type: Number,
        default: 4,
        validator: (val) => {
          return 0 <= val;
        }
      },
      buttonAlwaysVisible: {
        type: Boolean,
        default: false,
      },
    },
    watch: {
      offset: {
        immediate: true,
        handler: function () {
          this.initialize();
        }
      },
      limit: {
        immediate: true,
        handler: function () {
          this.initialize();
        }
      },
    },
    computed: {
      pageCount: function () {
        if (this.count) {
          return Math.ceil(this.count / this.limit);
        }
        return 1;
      },
      navSize: function () {
        if (this.size === null) {
          return "pagination";
        }
        return `pagination pagination-${this.size}`;
      },
      shortNavSize: function () {
        if (this.shortSize === null && this.size === null) {
          return "pagination";
        }
        if (this.shortSize) {
          return `pagination pagination-${this.shortSize}`;
        }
        return `pagination pagination-${this.size}`;
      },
      shortClass: function() {
        if (this.breakpoint === null) {
          return "d-none";
        }
        return `d-block d-${this.breakpoint}-none`;
      },
      longClass: function () {
        if (this.breakpoint === null) {
          return "d-block";
        }
        return `d-none d-${this.breakpoint}-block`;
      },
      infoClass: function () {
        if (this.breakpoint === null) {
          return "d-flex";
        }
        return `d-none d-${this.breakpoint}-flex`;
      },
      prevButtonActiveClass: function () {
        return {
          "disabled": this.selectedPageNumber <= 1,
        }
      },
      nextButtonActiveClass: function () {
        return {
          "disabled": this.selectedPageNumber >= this.pageCount,
        }
      },
      prevButtonVisible: function () {
        return this.buttonAlwaysVisible || this.selectedPageNumber !== 1;
      },
      nextButtonVisible: function() {
        return this.buttonAlwaysVisible || this.selectedPageNumber !== this.pageCount;
      },
      startCount: function() {
        return this.offset + 1;
      },
      endCount: function() {
        let n = this.offset + this.limit;
        return n > this.count ? this.count : n;
      },
      overStartRange: function () {
        if (this.range === 0) {
          return false;
        }
        return this.selectedPageNumber - this.range > 1;
      },
      overEndRange: function () {
        if (this.range === 0) {
          return false;
        }
        return this.selectedPageNumber + this.range < this.pageCount;
      },
      informationVisible: function(){
        return this.visibleInfo && this.count;
      },
      limitVisible: function () {
        return this.visibleLimit && this.count;
      },
      limitCount: {
        get: function () {
          if (this.status.limit != null && Number.isFinite(this.status.limit)) {
            return this.status.limit;
          }
          return 20;
        },
        set: function (val) {
          if (val != null && Number.isFinite(Number(val)) && Number(val) > 0) {
            this.status.limit = Number(val);
            return;
          }
          this.status.limit = 20;
        },
      },
    },
    data: () => {
      return {
        status: {
          paginationOffset: 0,
          limit: 20,
        },
        selectedPageNumber: 1,
        fromOutside: true,
      }
    },
    methods: {
      initialize() {
        if (this.fromOutside) {
          this.selectedPageNumber = 1;
        }
        this.fromOutside = true;
      },
      movePage(page) {
        if (this.selectedPageNumber === page) {
          return;
        }
        this.selectedPageNumber = page;
        this.status.paginationOffset = (page - 1) * this.limit;
        this.fromOutside = false;
        this.$emit("move", this.status);
      },
      moveNext() {
        if (this.selectedPageNumber >= this.pageCount) {
          return;
        }
        let num = this.selectedPageNumber + 1;
        this.movePage(num);
      },
      movePrevent() {
        if (this.selectedPageNumber <= 1) {
          return;
        }
        let num = this.selectedPageNumber - 1;
        this.movePage(num);
      },
      limitChange() {
        this.$emit("limitChange", this.status);
      },
      inRange(num) {
        if (this.range === 0) {
          return true;
        }
        let startRange = this.selectedPageNumber - this.range;
        let endRange = this.selectedPageNumber + this.range;
        return startRange <= num && num <= endRange;
      }
    },
  });
</script>
